import { useEffect, useRef, useState } from "react";

/**
 * Phase M.89 (Mar 2026) — Android Google Sign-In bridge.
 *
 * The Android app launches Chrome Custom Tabs pointed at Emergent's
 * auth URL with our PWA's `/android-oauth` path as the redirect.
 * When the OAuth flow completes, Emergent lands the browser back
 * here with `#session_id=xxx` in the URL fragment. This component
 * reads that fragment and immediately deep-links out to
 * `aura://google-signin` so the Android app can pick up the
 * session_id and hand it to `/api/auth/google/session` for the
 * token exchange.
 *
 * We intentionally DO NOT call `/api/auth/google/session` from this
 * page — the token needs to land in the Android app's memory (via
 * AuthTokenStore), not the PWA's cookie jar. The Android side owns
 * the token exchange from here.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
export function AndroidOAuthBridge() {
  const done = useRef(false);
  const [state, setState] = useState("Opening Aura…");

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setState("No session_id found. You can close this tab.");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    // Deep-link back into the Android app. Custom scheme registered in
    // AndroidManifest.xml (`aura://google-signin`). Assign to
    // window.location so the browser fires the ACTION_VIEW intent.
    window.location.href = `aura://google-signin?session_id=${encodeURIComponent(sessionId)}`;
    // If the deep-link doesn't fire (e.g. app was uninstalled between
    // opening the tab and returning), show a friendly fallback so the
    // user isn't stuck on a blank page.
    const t = setTimeout(() => {
      setState("Signed in. You can close this tab and return to Aura.");
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="app-container">
      <div className="wallpaper-placeholder">
        <h1>✨</h1>
        <p>{state}</p>
      </div>
    </div>
  );
}
